/** @jsxImportSource react */
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { reportsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { entityLabelPlural, formatRelativeTime } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'

const SCHEDULE = { WEEKLY: 'Emailed weekly', MONTHLY: 'Emailed monthly' }

/** Saved reports: mine and the ones shared with the workspace. */
const ReportsPage = () => {
  const tenantId = useTenantKey()
  const navigate = useNavigate()
  const { data = [], isLoading, error } = useQuery({ queryKey: [tenantId, 'reports'], queryFn: reportsAPI.list })
  return (
    <Box sx={{ maxWidth: 900 }}>
      <PageHeader title="Reports" subtitle="Counts and totals by stage, person, source or month — saved, shared and emailed."
        actions={<Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/reports/new')}>New report</Button>} />
      {error && <Alert severity="error">{getErrorMessage(error)}</Alert>}
      {isLoading ? <Loading /> : data.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">No reports yet. Make one, for example “Deals by stage, total amount”.</Typography>
        </Paper>
      ) : (
        <Paper variant="outlined">
          {data.map((r, i) => (
            <Box key={r.id} onClick={() => navigate(`/reports/${r.id}`)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, cursor: 'pointer', borderTop: i ? 1 : 0, borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' }, flexWrap: 'wrap' }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name}</Typography>
                <Typography variant="caption" color="text.secondary">{entityLabelPlural(r.entityType)} · updated {formatRelativeTime(r.updatedOn)}</Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                {r.shared && <Chip size="small" label="Shared" variant="outlined" />}
                {SCHEDULE[r.schedule] && <Chip size="small" label={SCHEDULE[r.schedule]} variant="outlined" />}
              </Stack>
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  )
}

export default ReportsPage
